/**
 * Virtual account shares API (TMMAPP-0005 / TMMAPP-0011, design §5.2–§5.4, §8.4) — wire types from
 * `src/api/generated/`; normalized into the module's domain type. A sharer's projection carries
 * `displayName` only (MM-D-34).
 */
import { request } from './client';
import { normalizeStanding, type MemberStanding } from './memberStanding';
import { personNameOrNull } from './personNames';
import type {
  ListResponseSharedWithMeItem,
  ReconcileResponse,
  ShareCreateRequest,
  SharePatchRequest,
  SharedWithMeItem as WireSharedWithMeItem,
  ShareResponse as WireShare,
} from './generated';

export type ShareStatus = 'INVITED' | 'ACTIVE' | 'REVOKED' | 'EXPIRED';
export type ShareSeat = 'SUBSCRIBER' | 'GUEST_SEAT';
/** A share's access (MM-D-115): VIEWER ("Read only") or MANAGER. */
export type ShareRole = 'VIEWER' | 'MANAGER';

export interface Share {
  id: number;
  userId: number | null;
  email: string | null;
  boundEmail: string | null;
  displayName: string | null;
  status: ShareStatus;
  /** The seat on the owner's view (TMMAPI-0143); `null` when unbound, seatless, or not served. */
  seat: ShareSeat | null;
  /** The role (TMMAPI-0146), on the owner's and the members' views. */
  role: ShareRole;
  /** The owner allows the assistant on this share (TMMAPI-0181 U1, MM-D-146); served on both views. */
  assistantAllowed: boolean;
  /**
   * The owner's view only (TMMAPI-0186 U1, MM-D-149): this member's monthly limit on the owner's AI
   * credit, in minor units of the owner's AI wallet currency; `0` = they may not ask; `null` = none.
   */
  assistantMonthlyLimitMinor: number | null;
  /** The owner's view only: what this member used of that credit this month; `null` when not served. */
  assistantUsedThisMonthMinor: number | null;
}

/** A row of `GET /v1/me/shared-with-me` — the caller's own ACTIVE share on someone else's virtual account. */
export interface SharedWithMeItem extends MemberStanding {
  shareId: number;
  virtualAccountId: number;
  virtualAccountName: string;
  /** The owner's display name; `null` when they have none (render with `personName`). */
  ownerDisplayName: string | null;
  /**
   * The owner's user id (TMMAPI-0142, required by the contract): a grouping and equality key only,
   * never rendered or sent.
   */
  ownerUserId: number;
  currency: string;
}

const SHARE_STATUSES: ReadonlySet<string> = new Set(['INVITED', 'ACTIVE', 'REVOKED', 'EXPIRED']);
const SHARE_SEATS: ReadonlySet<string> = new Set(['SUBSCRIBER', 'GUEST_SEAT']);

/** A share row; `null` (dropped) with a status this app does not know. */
export function normalizeShare(w: WireShare): Share | null {
  if (!SHARE_STATUSES.has(w.status)) return null;
  return {
    id: w.id,
    userId: w.userId ?? null,
    email: w.email ?? null,
    boundEmail: w.boundEmail ?? null,
    displayName: personNameOrNull(w.displayName),
    status: w.status,
    seat: w.seat !== undefined && SHARE_SEATS.has(w.seat) ? w.seat : null,
    role: w.role === 'MANAGER' ? 'MANAGER' : 'VIEWER',
    assistantAllowed: w.assistantAllowed,
    assistantMonthlyLimitMinor: w.assistantMonthlyLimitMinor ?? null,
    assistantUsedThisMonthMinor: w.assistantUsedThisMonthMinor ?? null,
  };
}

export function normalizeShares(value: readonly WireShare[]): Share[] {
  return value.flatMap((row) => {
    const share = normalizeShare(row);
    return share ? [share] : [];
  });
}

function normalizeSharedWithMeItem(w: WireSharedWithMeItem): SharedWithMeItem {
  return {
    shareId: w.shareId,
    virtualAccountId: w.virtualAccountId,
    virtualAccountName: w.virtualAccountName,
    ownerDisplayName: personNameOrNull(w.ownerDisplayName),
    ownerUserId: w.ownerUserId,
    currency: w.currency ?? '',
    ...normalizeStanding(w),
  };
}

function knownShare(share: Share | null): Share {
  if (!share) throw new Error('share status is unknown');
  return share;
}

export const sharesApi = {
  /** Bind only the caller's accepted invitations; visibility is reloaded from /me. */
  async reconcile(): Promise<{
    reconciled: number;
    virtualAccounts: number[];
    upstreamUnavailable: boolean;
  }> {
    const wire = await request<ReconcileResponse>('POST', '/v1/me/shares/reconcile');
    return {
      reconciled: wire.reconciled,
      virtualAccounts: wire.virtualAccounts,
      upstreamUnavailable: wire.upstream === 'unavailable',
    };
  },
  /** GET /v1/virtual-accounts/{id}/shares. */
  async list(virtualAccountId: number): Promise<Share[]> {
    return normalizeShares(
      await request<WireShare[]>('GET', `/v1/virtual-accounts/${virtualAccountId}/shares`),
    );
  },
  /**
   * POST /v1/virtual-accounts/{id}/shares — `ACTIVE` when the address is a known user, else
   * `INVITED`; `role` defaults to VIEWER. Sends only the keys given.
   */
  async create(
    virtualAccountId: number,
    body: ShareCreateRequest,
    idempotencyKey?: string,
  ): Promise<Share> {
    return knownShare(
      normalizeShare(
        await request<WireShare>('POST', `/v1/virtual-accounts/${virtualAccountId}/shares`, {
          json: body,
          ...(idempotencyKey !== undefined ? { idempotencyKey } : {}),
        }),
      ),
    );
  },
  /** PATCH /v1/virtual-accounts/{id}/shares/{shareId} — the owner sets an INVITED or ACTIVE share's role. */
  async updateRole(virtualAccountId: number, shareId: number, role: ShareRole): Promise<Share> {
    const body: SharePatchRequest = { role };
    return knownShare(
      normalizeShare(
        await request<WireShare>(
          'PATCH',
          `/v1/virtual-accounts/${virtualAccountId}/shares/${shareId}`,
          { json: body },
        ),
      ),
    );
  },
  /**
   * PATCH /v1/virtual-accounts/{id}/shares/{shareId} `{assistantAllowed}` — the owner's assistant
   * grant on a share (TMMAPI-0181 U1). Sends only that field (`role` is optional). A guest seat may
   * be granted too since TMMAPI-0195 U4 (G49): `ASSISTANT_GUEST_NOT_ALLOWED` no longer answers here.
   */
  async setAssistantAllowed(
    virtualAccountId: number,
    shareId: number,
    assistantAllowed: boolean,
  ): Promise<Share> {
    const body: SharePatchRequest = { assistantAllowed };
    return knownShare(
      normalizeShare(
        await request<WireShare>(
          'PATCH',
          `/v1/virtual-accounts/${virtualAccountId}/shares/${shareId}`,
          { json: body },
        ),
      ),
    );
  },
  /**
   * PATCH /v1/virtual-accounts/{id}/shares/{shareId} `{assistantMonthlyLimitMinor}` — the owner's
   * monthly limit for this member on their AI credit (TMMAPI-0186 U1): an amount, `0` (they may not
   * ask) or `null` (no limit). Sends only that field.
   */
  async setAssistantMonthlyLimit(
    virtualAccountId: number,
    shareId: number,
    assistantMonthlyLimitMinor: number | null,
  ): Promise<Share> {
    const body: SharePatchRequest = { assistantMonthlyLimitMinor };
    return knownShare(
      normalizeShare(
        await request<WireShare>(
          'PATCH',
          `/v1/virtual-accounts/${virtualAccountId}/shares/${shareId}`,
          { json: body },
        ),
      ),
    );
  },
  /** POST /v1/virtual-accounts/{id}/shares/{shareId}/resend. */
  async resend(virtualAccountId: number, shareId: number): Promise<Share> {
    return knownShare(
      normalizeShare(
        await request<WireShare>(
          'POST',
          `/v1/virtual-accounts/${virtualAccountId}/shares/${shareId}/resend`,
        ),
      ),
    );
  },
  /** DELETE /v1/virtual-accounts/{id}/shares/{shareId} — revoke (owner) or leave (the sharer's own row). */
  async remove(virtualAccountId: number, shareId: number): Promise<void> {
    await request<unknown>('DELETE', `/v1/virtual-accounts/${virtualAccountId}/shares/${shareId}`);
  },
  /** GET /v1/me/shared-with-me — the wire wraps the rows in `{ items }`. */
  async sharedWithMe(): Promise<SharedWithMeItem[]> {
    const wire = await request<ListResponseSharedWithMeItem>('GET', '/v1/me/shared-with-me');
    return wire.items.map(normalizeSharedWithMeItem);
  },
};
